import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { activityRequest, activitySettingsSchema, ActivitySettings } from './contracts';

function SettingsForm({
  initial,
  reload,
}: {
  initial: ActivitySettings;
  reload: () => Promise<ActivitySettings>;
}) {
  const client = useQueryClient();
  const [base, setBase] = useState(initial);
  const [tolerance, setTolerance] = useState(initial.reportingToleranceMinutes?.toString() || '');
  const [allowance, setAllowance] = useState(String(initial.weeklyAllowanceMinutes));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const replace = (value: ActivitySettings) => {
    setBase(value);
    setTolerance(value.reportingToleranceMinutes?.toString() || '');
    setAllowance(String(value.weeklyAllowanceMinutes));
  };
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const result = await activityRequest(
        'SAVE_MACHINE_ACTIVITY_SETTINGS',
        {
          orgId: base.orgId,
          sensorId: base.sensorId,
          expectedVersion: base.version,
          config: {
            reportingToleranceMinutes: tolerance === '' ? null : Number(tolerance),
            weeklyAllowanceMinutes: Number(allowance),
          },
        },
        activitySettingsSchema
      );
      replace(result);
      setSaved(true);
      await client.invalidateQueries({
        queryKey: ['machine-activity-settings', base.orgId, base.sensorId],
      });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Kunne ikke gemme.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="space-y-4" onSubmit={save}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block space-y-1">
          Maksimal pause mellem rapporter (minutter)
          <Input
            type="number"
            min={1}
            max={10080}
            step="any"
            disabled={busy}
            value={tolerance}
            onChange={(e) => {
              setTolerance(e.target.value);
              setSaved(false);
            }}
          />
        </label>
        <label className="block space-y-1">
          Ventetid efter ugens afslutning (minutter)
          <Input
            type="number"
            required
            min={0}
            max={10080}
            step="any"
            disabled={busy}
            value={allowance}
            onChange={(e) => {
              setAllowance(e.target.value);
              setSaved(false);
            }}
          />
        </label>
      </div>
      <p className="text-sm text-muted-foreground">
        Angiv den forventede rapporteringsfrist, før alarmer aktiveres. Huller i rapporteringen gør
        aktivitetsvurderingen ukendt. Ugen er mandag–søndag; forsinkede rapporter tæller på
        modtagelsesdagen.
      </p>
      {error && (
        <div role="alert">
          <p className="text-red-700">{error}</p>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                replace(await reload());
                setError('');
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Kunne ikke genindlæse.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Hent seneste version og erstat mine ændringer
          </Button>
        </div>
      )}
      {saved && <p role="status">Rapporteringsindstillingerne er gemt.</p>}
      <Button type="submit" disabled={busy}>
        {busy ? 'Gemmer…' : 'Gem rapporteringsindstillinger'}
      </Button>
    </form>
  );
}

export default function ActivityReportingSettings({
  orgId,
  sensorId,
}: {
  orgId: string;
  sensorId: string;
}) {
  const query = useQuery({
    queryKey: ['machine-activity-settings', orgId, sensorId],
    queryFn: () =>
      activityRequest('GET_MACHINE_ACTIVITY_SETTINGS', { orgId, sensorId }, activitySettingsSchema),
  });
  return (
    <section className="ua-card space-y-3" aria-label="Rapportering for maskinaktivitet">
      <h3>Rapportering og datagrundlag</h3>
      {query.isPending ? (
        <p role="status">Henter rapporteringsindstillinger…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>Kunne ikke hente indstillinger.</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Prøv igen
          </Button>
        </div>
      ) : (
        <SettingsForm
          key={`${orgId}/${sensorId}`}
          initial={query.data}
          reload={async () => {
            const result = await query.refetch();
            if (!result.data || result.isError)
              throw result.error || new Error('Kunne ikke genindlæse.');
            return result.data;
          }}
        />
      )}
    </section>
  );
}
